import { ApiError } from '../api/client';
import { SIGNIN_TEXTS, type SignInLocale, type SignInTexts } from './signin-i18n';

/**
 * Message d'échec des pages d'authentification (ADR-091, ADR-092 §7) : jamais un code HTTP brut.
 * Échec de connexion (réponse indiscernable), débit (`Retry-After`), service injoignable ou en
 * erreur 5xx (le relais de développement répond 500 pendant un redémarrage de l'API) ; un
 * message de validation du serveur (politique de mot de passe…) est rendu tel quel. Le code
 * technique ne va qu'au détail repliable.
 */
export interface AuthFailure {
  text: string;
  detail?: string;
}

const RAW_HTTP = /^(Erreur|Error|HTTP)\s*\d{3}\.?$/i;

export function authFailure(error: unknown, locale: SignInLocale = 'fr', t: SignInTexts = SIGNIN_TEXTS[locale]): AuthFailure {
  if (!(error instanceof ApiError)) return { text: t.unexpected };
  const detail = error.status > 0 ? `${error.code} (HTTP ${error.status})` : error.code;
  if (error.code === 'LOGIN_FAILED') return { text: t.loginFailed, detail };
  if (error.status === 429 || error.code === 'RATE_LIMITED') {
    const minutes = error.retryAfterSeconds === undefined ? undefined : Math.max(1, Math.ceil(error.retryAfterSeconds / 60));
    return { text: minutes === undefined ? t.rateLimited : t.rateLimitedIn.replace('{n}', String(minutes)), detail };
  }
  if (error.status === 0 || error.status >= 500 || error.code === 'NETWORK_UNAVAILABLE') return { text: t.unavailable, detail };
  if (error.code === 'MFA_CHALLENGE_INVALID') return { text: t.challengeExpired, detail };
  if (error.code === 'MFA_LOCKED') return { text: t.mfaLocked, detail };
  if (error.code === 'MFA_CODE_INVALID') return { text: t.codeInvalid, detail };
  if (error.message === '' || RAW_HTTP.test(error.message.trim())) return { text: t.unexpected, detail };
  return { text: error.message, detail };
}
